import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import dataService from '../services/dataService';

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const toDatetimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const DailyTasksPopup = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [editingTask, setEditingTask] = useState(null);
  const [editTaskExpandedItems, setEditTaskExpandedItems] = useState(new Set());
  const [hoveredCircle, setHoveredCircle] = useState(null);
  const shownForEmail = useRef(null);
  const pickerOpenRef = useRef(false);

  useEffect(() => {
    if (!user?.email) { shownForEmail.current = null; return; }
    if (shownForEmail.current === user.email) return;
    shownForEmail.current = user.email;
    setShow(true);
  }, [user?.email]);

  useEffect(() => {
    if (!show || !user) return;

    const loadTasks = async () => {
      setLoading(true);
      try {
        let rawRecords = null;
        if (user.role === 'member' && user.email) {
          const result = await dataService.getTasksByMembers([user.email.trim().toLowerCase()]);
          rawRecords = result?.tasks ?? [];
        } else {
          const result = await dataService.getOwnerTasks();
          rawRecords = result?.tasks ?? [];
        }
        setRecords(buildFiltered(rawRecords));
      } catch {
        setRecords([]);
      } finally {
        setLoading(false);
      }
    };

    loadTasks();
  }, [show, user]);

  const buildFiltered = (rawRecords) => {
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return (rawRecords ?? [])
      .map(r => {
        const todayTasks = (r.tasks || []).filter(t => t.due && t.status !== 'completed' && isSameDay(new Date(t.due), today));
        const overdueTasks = (r.tasks || []).filter(t => {
          if (!t.due || t.status === 'completed') return false;
          const d = new Date(t.due);
          return new Date(d.getFullYear(), d.getMonth(), d.getDate()) < todayStart;
        });
        return { ...r, todayTasks, overdueTasks };
      })
      .filter(r => r.todayTasks.length > 0 || r.overdueTasks.length > 0);
  };

  const dismiss = () => setShow(false);
  const goToTasks = () => { dismiss(); navigate('/tasks'); };

  const handleOpenEdit = (task, record) => {
    const taskIdx = (record.tasks || []).findIndex(t =>
      t.task_id ? t.task_id === task.task_id : t === task
    );
    setEditingTask({ task: { ...task }, originalTask: { ...task }, record, taskIdx });
    setEditTaskExpandedItems(new Set());
  };

  const handleToggleComplete = (task, record) => {
    const taskIdx = (record.tasks || []).findIndex(t =>
      t.task_id ? t.task_id === task.task_id : t === task
    );
    if (taskIdx < 0) return;
    const title = record.task_list_title;
    const stampedTask = {
      ...task,
      task_id: task.task_id || `tid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      status: task.status === 'completed' ? 'notStarted' : 'completed',
      update_timestamp: new Date().toISOString(),
    };
    setRecords(prev =>
      prev.map(r => {
        if (r.task_list_title !== title) return r;
        const updatedTasks = (r.tasks || []).map((t, i) => i === taskIdx ? stampedTask : t);
        return { ...r, ...buildFiltered([{ ...r, tasks: updatedTasks }])[0] };
      }).filter(r => r.todayTasks.length > 0 || r.overdueTasks.length > 0)
    );
    dataService.updateTaskItemDraft(title, taskIdx, stampedTask).then(ok => {
      if (!ok) console.warn('[DailyTasksPopup] Toggle complete draft save may have failed');
    });
  };

  const handleTaskSave = () => {
    if (!editingTask) return;
    const { task, taskIdx, record } = editingTask;
    const title = record.task_list_title;

    const stampedTask = {
      ...task,
      task_id: task.task_id || `tid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      update_timestamp: new Date().toISOString(),
    };

    setRecords(prev =>
      prev.map(r => {
        if (r.task_list_title !== title) return r;
        const updatedTasks = (r.tasks || []).map((t, i) => i === taskIdx ? stampedTask : t);
        return { ...r, ...buildFiltered([{ ...r, tasks: updatedTasks }])[0] };
      }).filter(r => r.todayTasks.length > 0 || r.overdueTasks.length > 0)
    );

    setEditingTask(null);

    if (taskIdx >= 0) {
      dataService.updateTaskItemDraft(title, taskIdx, stampedTask).then(ok => {
        if (!ok) console.warn('[DailyTasksPopup] Draft save may have failed');
      });
    }
  };

  if (!show) return null;

  const today = new Date();

  const renderTask = (task, overdue, isLast, record) => {
    const isCompleted = task.status === 'completed';
    const isInProgress = task.status === 'inProgress';
    const accentColor  = overdue ? '#ef4444' : isCompleted ? '#22c55e' : isInProgress ? '#f59e0b' : '#94a3b8';
    const statusBg     = overdue ? '#fff5f5' : isCompleted ? '#f0fdf4' : isInProgress ? '#fffbeb' : '#ffffff';
    const statusBorder = overdue ? '#fecaca' : isCompleted ? '#bbf7d0' : isInProgress ? '#fde68a' : '#e2e8f0';
    const dueDate      = task.due ? new Date(task.due) : null;
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const daysUntilDue = dueDate
      ? Math.round((new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate()) - todayMidnight) / 86400000)
      : null;
    const badgeText = daysUntilDue === null ? ''
      : daysUntilDue < 0 ? `! Overdue ${Math.abs(daysUntilDue)}d`
      : 'Due today';
    const badgeBg = daysUntilDue !== null && daysUntilDue < 0 ? '#ef4444' : '#f97316';
    const exactDateLabel = dueDate
      ? dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : '';
    const dueLineColor = isCompleted ? '#22c55e' : badgeBg;
    const dueLineText = isCompleted
      ? (dueDate ? `Due: ${exactDateLabel} — Completed` : 'Completed')
      : !dueDate ? 'No due date'
      : `Due: ${exactDateLabel} — ${badgeText}`;
    const circleKey    = `${task.task_id || task.title}__circle`;
    const isCircleHovered = hoveredCircle === circleKey;
    return (
      <div
        key={task.task_id || task.title}
        onClick={() => handleOpenEdit(task, record)}
        style={{
          background: statusBg, borderRadius: '8px', padding: '9px 11px',
          marginBottom: isLast ? 0 : '6px',
          border: `1px solid ${statusBorder}`, borderLeft: `3px solid ${accentColor}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          cursor: 'pointer', transition: 'box-shadow 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.12)'; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          {/* Completion checkbox */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={e => { e.stopPropagation(); handleToggleComplete(task, record); }}
              onMouseEnter={() => setHoveredCircle(circleKey)}
              onMouseLeave={() => setHoveredCircle(null)}
              aria-label={isCompleted ? 'Mark uncompleted' : 'Mark completed'}
              style={{
                width: '20px', height: '20px', borderRadius: '4px',
                border: `2px solid ${isCompleted
                  ? (isCircleHovered ? 'rgba(34,197,94,0.6)' : '#22c55e')
                  : (isCircleHovered ? '#22c55e' : '#94a3b8')}`,
                background: isCompleted
                  ? (isCircleHovered ? 'rgba(34,197,94,0.65)' : '#22c55e')
                  : (isCircleHovered ? 'rgba(34,197,94,0.18)' : 'transparent'),
                color: isCompleted ? 'white' : (isCircleHovered ? '#15803d' : '#d1d5db'),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: 800, cursor: 'pointer',
                transition: 'all 0.15s', outline: 'none', padding: 0,
                transform: isCircleHovered ? 'scale(1.12)' : 'scale(1)',
                boxShadow: isCircleHovered ? '0 0 0 3px rgba(34,197,94,0.18)' : 'none',
              }}
            >✓</button>
            {isCircleHovered && (
              <div style={{
                position: 'absolute', top: '50%', left: '26px',
                transform: 'translateY(-50%)',
                background: 'rgba(30,41,59,0.95)', color: 'white',
                fontSize: '11px', fontWeight: 500,
                padding: '4px 8px', borderRadius: '5px',
                whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 2200,
                boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
              }}>
                {isCompleted ? 'Mark uncompleted' : 'Mark completed'}
                <div style={{
                  position: 'absolute', top: '50%', right: '100%',
                  transform: 'translateY(-50%)',
                  width: 0, height: 0,
                  borderTop: '5px solid transparent',
                  borderBottom: '5px solid transparent',
                  borderRight: '5px solid rgba(30,41,59,0.95)',
                }} />
              </div>
            )}
          </div>
          <div style={{ fontWeight: 700, fontSize: '13px', color: '#1e293b', lineHeight: '1.3', textDecoration: isCompleted ? 'line-through' : 'none', opacity: isCompleted ? 0.6 : 1, flex: 1 }}>
            {task.title}
          </div>
          <button
            onClick={e => { e.stopPropagation(); handleOpenEdit(task, record); }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(34,197,94,0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 8px rgba(34,197,94,0.35)'; }}
            onMouseDown={e => { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(34,197,94,0.3)'; }}
            onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(34,197,94,0.5)'; }}
            style={{
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              border: 'none', borderRadius: '8px',
              color: 'white', fontSize: '11px', fontWeight: 700,
              height: '24px', padding: '0 10px', cursor: 'pointer', flexShrink: 0,
              letterSpacing: '0.3px', transition: 'all 0.15s ease',
              boxShadow: '0 2px 8px rgba(34,197,94,0.35)',
            }}
          >Edit</button>
        </div>
        <div style={{ textAlign: 'left', paddingLeft: '28px', marginBottom: task.action_items?.length > 0 ? '4px' : 0 }}>
          <span style={{ fontSize: '12px', color: dueLineColor, fontWeight: 700 }}>
            {dueLineText}
          </span>
        </div>
        {task.action_items?.length > 0 && (
          <div style={{ borderTop: `1px solid ${statusBorder}`, paddingTop: '5px', paddingLeft: '28px', marginTop: '2px' }}>
            {task.action_items.map((item, ii) => (
              <div key={ii} style={{ display: 'grid', gridTemplateColumns: '12px 1fr', gap: '4px', marginBottom: ii < task.action_items.length - 1 ? '3px' : 0 }}>
                <span style={{ color: accentColor, fontSize: '11px', fontWeight: 700, lineHeight: '1.5', textAlign: 'center' }}>›</span>
                <span style={{ fontSize: '11px', color: '#475569', lineHeight: '1.5' }}>{item}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <style>{`
        @keyframes dtp-fadein { from { opacity: 0; transform: translate(-50%,-50%) scale(0.96); } to { opacity: 1; transform: translate(-50%,-50%) scale(1); } }
        @keyframes dtp-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>

      <div onClick={editingTask ? undefined : dismiss} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(3px)', zIndex: 2000,
      }} />

      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 2001, width: '480px', maxWidth: '94vw',
        borderRadius: '16px', overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
        display: 'flex', flexDirection: 'column',
        maxHeight: '80vh',
        animation: 'dtp-fadein 0.2s ease',
      }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(145deg, #1a2540 0%, #243050 60%, #1e3a5f 100%)',
          padding: '20px 22px 16px', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '11px', color: '#7b93b8', fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '4px' }}>
                Daily Briefing
              </div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#ffffff', lineHeight: 1.3 }}>
                Due & Overdue Tasks
              </div>
              <div style={{ fontSize: '13px', color: '#8faac4', marginTop: '4px' }}>
                {today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
            <button onClick={dismiss} style={{
              background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%',
              width: '30px', height: '30px', color: '#8faac4', cursor: 'pointer',
              fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, marginLeft: '12px',
            }}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{
          background: '#f1f4f8', overflowY: 'auto', flex: 1,
          padding: loading || records.length === 0 ? '0' : '14px 14px 8px',
        }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', gap: '14px' }}>
              <div style={{ width: '28px', height: '28px', border: '3px solid #e2e8f0', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'dtp-spin 0.9s linear infinite' }} />
              <span style={{ fontSize: '14px', color: '#64748b' }}>Loading tasks…</span>
            </div>
          ) : records.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '44px 20px', gap: '12px' }}>
              <div style={{ fontSize: '44px' }}>✓</div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>You're all clear!</div>
              <div style={{ fontSize: '13px', color: '#64748b', textAlign: 'center' }}>No tasks are due or overdue. Enjoy your day!</div>
            </div>
          ) : (
            records.map((record, idx) => (
              <div key={idx} style={{ borderRadius: '10px', overflow: 'hidden', marginBottom: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.09)', border: '1px solid rgba(0,0,0,0.06)' }}>
                {/* Property header */}
                <div style={{ background: 'linear-gradient(145deg, #1a2540 0%, #243050 60%, #1e2d4a 100%)', padding: '12px 14px' }}>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: '#ffffff', lineHeight: '1.4', marginBottom: '2px' }}>
                    {record.property_address || record.task_list_title}
                  </div>
                  <div style={{ fontSize: '11px', color: '#a4afbe' }}>{record.id}</div>
                </div>

                <div style={{ padding: '10px', background: '#f1f4f8' }}>
                  {record.overdueTasks.length > 0 && (
                    <>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
                        Overdue ({record.overdueTasks.length})
                      </div>
                      {record.overdueTasks.map((task, ti) =>
                        renderTask(task, true, ti === record.overdueTasks.length - 1 && record.todayTasks.length === 0, record)
                      )}
                    </>
                  )}

                  {record.overdueTasks.length > 0 && record.todayTasks.length > 0 && (
                    <div style={{ height: '1px', background: '#e2e8f0', margin: '8px 0' }} />
                  )}

                  {record.todayTasks.length > 0 && (
                    <>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} />
                        Due Today ({record.todayTasks.length})
                      </div>
                      {record.todayTasks.map((task, ti) =>
                        renderTask(task, false, ti === record.todayTasks.length - 1, record)
                      )}
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div style={{
          background: 'white', borderTop: '1px solid #e2e8f0',
          padding: '14px 16px', display: 'flex', gap: '10px', flexShrink: 0,
        }}>
          <button onClick={dismiss} style={{
            flex: 1, padding: '10px', borderRadius: '8px',
            border: '1px solid #dadce0', background: 'white',
            color: '#3c4043', fontWeight: 500, fontSize: '14px', cursor: 'pointer',
          }}>
            Dismiss
          </button>
          <button onClick={goToTasks} style={{
            flex: 1, padding: '10px', borderRadius: '8px',
            border: 'none', background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
            color: 'white', fontWeight: 600, fontSize: '14px', cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(59,130,246,0.35)',
          }}>
            View All Tasks →
          </button>
        </div>
      </div>

      {/* Task edit modal — renders above the popup */}
      {editingTask && (() => {
        const et = editingTask.task;
        const isCompleted  = et.status === 'completed';
        const statusColor  = isCompleted ? '#22c55e' : '#94a3b8';
        const statusBorder = isCompleted ? '#bbf7d0' : '#e2e8f0';
        const isDirty = JSON.stringify(et) !== JSON.stringify(editingTask.originalTask);
        const isModalHovered = hoveredCircle === '__modal__';

        return (
          <>
            <div onClick={() => setEditingTask(null)} style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 2100, backdropFilter: 'blur(2px)',
            }} />

            <div style={{
              position: 'fixed', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2101, width: '420px', maxWidth: '94vw',
              backgroundColor: 'white', borderRadius: '14px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              border: `2px solid ${statusColor}`,
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>

              {/* Header */}
              <div style={{ background: 'linear-gradient(145deg, #1a2540 0%, #243050 100%)', padding: '14px 18px' }}>
                <div style={{ fontWeight: 700, fontSize: '13px', color: '#fff' }}>
                  {editingTask.record.task_list_title}
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

                {/* Title + completion checkbox */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ position: 'relative', flexShrink: 0, marginTop: '2px' }}>
                    <button
                      onClick={() => setEditingTask(prev => ({ ...prev, task: { ...prev.task, status: prev.task.status === 'completed' ? 'notStarted' : 'completed' } }))}
                      onMouseEnter={() => setHoveredCircle('__modal__')}
                      onMouseLeave={() => setHoveredCircle(null)}
                      onFocus={() => setHoveredCircle('__modal__')}
                      onBlur={() => setHoveredCircle(null)}
                      aria-label={isCompleted ? 'Mark task uncompleted' : 'Mark task completed'}
                      style={{
                        width: '22px', height: '22px', borderRadius: '4px',
                        border: `2px solid ${isCompleted ? (isModalHovered ? 'rgba(34,197,94,0.6)' : '#22c55e') : (isModalHovered ? '#22c55e' : '#94a3b8')}`,
                        background: isCompleted ? (isModalHovered ? 'rgba(34,197,94,0.65)' : '#22c55e') : (isModalHovered ? 'rgba(34,197,94,0.12)' : 'transparent'),
                        color: isCompleted ? 'white' : (isModalHovered ? '#22c55e' : '#d1d5db'),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', fontWeight: 800, cursor: 'pointer',
                        transition: 'all 0.15s', outline: 'none', padding: 0,
                        transform: isModalHovered ? 'scale(1.12)' : 'scale(1)',
                        boxShadow: isModalHovered ? '0 0 0 3px rgba(34,197,94,0.18)' : 'none',
                      }}
                    >✓</button>
                    {isModalHovered && (
                      <div style={{
                        position: 'absolute', top: '1px', left: '28px',
                        background: 'rgba(30,41,59,0.95)', color: 'white',
                        fontSize: '11px', fontWeight: 500,
                        padding: '4px 8px', borderRadius: '5px',
                        whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 2200,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                      }}>
                        {isCompleted ? 'Mark uncompleted' : 'Mark completed'}
                        <div style={{
                          position: 'absolute', top: '50%', right: '100%',
                          transform: 'translateY(-50%)',
                          width: 0, height: 0,
                          borderTop: '5px solid transparent',
                          borderBottom: '5px solid transparent',
                          borderRight: '5px solid rgba(30,41,59,0.95)',
                        }} />
                      </div>
                    )}
                  </div>
                  <div style={{
                    flex: 1, fontWeight: 700, fontSize: '16px', color: '#1e293b',
                    lineHeight: '1.35',
                    textDecoration: isCompleted ? 'line-through' : 'none',
                    opacity: isCompleted ? 0.6 : 1,
                  }}>
                    {et.title}
                  </div>
                </div>

                {/* Due date */}
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>Due Date</div>
                  <input
                    type="datetime-local"
                    value={toDatetimeLocal(et.due)}
                    onClick={e => { if (pickerOpenRef.current) { e.target.blur(); } else { try { e.target.showPicker(); pickerOpenRef.current = true; } catch {} } }}
                    onBlur={() => { pickerOpenRef.current = false; }}
                    onChange={e => { setEditingTask(prev => ({ ...prev, task: { ...prev.task, due: e.target.value ? new Date(e.target.value).toISOString() : null } })); e.target.blur(); }}
                    style={{
                      width: '100%', padding: '9px 12px', borderRadius: '8px',
                      border: `1.5px solid ${statusBorder}`, background: '#f8fafc',
                      fontSize: '13px', color: '#1e293b', outline: 'none',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  />
                </div>

                {/* Action items — expandable + editable */}
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>Action Items</div>
                  <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 12px', border: `1px solid ${statusBorder}`, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {(et.action_items || []).map((item, i) => {
                      const isExpanded = editTaskExpandedItems.has(i);
                      return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '14px 1fr 22px', gap: '6px', alignItems: isExpanded ? 'flex-start' : 'center' }}>
                          <span
                            onClick={() => setEditTaskExpandedItems(prev => {
                              const next = new Set(prev);
                              isExpanded ? next.delete(i) : next.add(i);
                              return next;
                            })}
                            style={{ color: statusColor, fontSize: '12px', fontWeight: 700, lineHeight: '1.5', textAlign: 'center', cursor: 'pointer', userSelect: 'none', marginTop: isExpanded ? '2px' : 0 }}
                          >{isExpanded ? '⌄' : '›'}</span>
                          {isExpanded ? (
                            <textarea
                              ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                              value={item}
                              rows={1}
                              onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                              onChange={e => setEditingTask(prev => {
                                const items = [...(prev.task.action_items || [])];
                                items[i] = e.target.value;
                                return { ...prev, task: { ...prev.task, action_items: items } };
                              })}
                              style={{ fontSize: '12px', color: '#1e293b', border: 'none', borderBottom: `1px solid ${statusBorder}`, background: 'transparent', outline: 'none', padding: '1px 2px', width: '100%', fontFamily: 'inherit', resize: 'none', overflow: 'hidden', lineHeight: '1.5', display: 'block' }}
                            />
                          ) : (
                            <span
                              onClick={() => setEditTaskExpandedItems(prev => { const next = new Set(prev); next.add(i); return next; })}
                              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                              style={{ fontSize: '12px', color: item ? '#1e293b' : '#cbd5e1', fontStyle: item ? 'normal' : 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', borderRadius: '3px', padding: '1px 2px', margin: '0 -2px' }}
                            >{item || 'click to edit'}</span>
                          )}
                          <button
                            onClick={() => {
                              setEditingTask(prev => {
                                const items = (prev.task.action_items || []).filter((_, j) => j !== i);
                                return { ...prev, task: { ...prev.task, action_items: items } };
                              });
                              setEditTaskExpandedItems(prev => {
                                const next = new Set();
                                for (const idx of prev) {
                                  if (idx < i) next.add(idx);
                                  else if (idx > i) next.add(idx - 1);
                                }
                                return next;
                              });
                            }}
                            title="Remove"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: '14px', lineHeight: 1, padding: 0 }}
                          >×</button>
                        </div>
                      );
                    })}
                    <button
                      onClick={() => {
                        const newIdx = (et.action_items || []).length;
                        setEditingTask(prev => ({
                          ...prev, task: { ...prev.task, action_items: [...(prev.task.action_items || []), ''] },
                        }));
                        setEditTaskExpandedItems(prev => { const next = new Set(prev); next.add(newIdx); return next; });
                      }}
                      style={{
                        marginTop: '4px', padding: '4px 10px', borderRadius: '6px',
                        border: `1.5px dashed ${statusColor}`, background: 'transparent',
                        color: statusColor, fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                        alignSelf: 'flex-start', letterSpacing: '0.3px',
                      }}
                    >+ Add item</button>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{
                display: 'flex', justifyContent: 'flex-end', gap: '10px',
                padding: '12px 20px', borderTop: '1px solid #f1f5f9', background: '#fafbfc',
              }}>
                <button onClick={() => setEditingTask(null)} style={{
                  padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0',
                  background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                }}>Cancel</button>
                <button onClick={handleTaskSave} disabled={!isDirty} style={{
                  padding: '8px 24px', borderRadius: '8px', border: 'none',
                  background: isDirty ? 'linear-gradient(135deg, #22c55e, #16a34a)' : '#e2e8f0',
                  color: isDirty ? 'white' : '#94a3b8', fontWeight: 700, fontSize: '13px',
                  cursor: isDirty ? 'pointer' : 'not-allowed',
                  boxShadow: isDirty ? '0 3px 10px rgba(34,197,94,0.4)' : 'none',
                }}>Save</button>
              </div>
            </div>
          </>
        );
      })()}
    </>
  );
};

export default DailyTasksPopup;
